import React, { useState } from "react";
import { FieldLabel } from "../../../common/u1/FieldLabel";
import { TagChip } from "../../../common/u1/TagChip";
import { SearchIcon, PlusIcon } from "../../../../assets/ui/icons";

/* Searchable multi-select for NACE 2.0 sub-industries. */
export const IndustryMultiSelect = ({
  id = "industry-search",
  options = [],
  value = [],
  onChange = () => {},
  isLoading = false,
  error,
  maxSuggestions = 6,
}) => {
  const [query, setQuery] = useState("");

  const chosen = new Set(value.map((v) => v.value));
  const matches = query
    ? options
        .filter(
          (o) =>
            !chosen.has(o.value) &&
            String(o.label).toLowerCase().includes(query.toLowerCase())
        )
        .slice(0, maxSuggestions)
    : [];

  const add = (option) => {
    onChange([...value, option]);
    setQuery("");
  };
  const remove = (option) => onChange(value.filter((v) => v.value !== option.value));

  return (
    <div>
      <FieldLabel htmlFor={id} required>
        Industry classification
      </FieldLabel>

      {value.length > 0 && (
        <ul className="mt-2 mb-2 flex flex-wrap gap-1.5 list-none p-0">
          {value.map((v) => (
            <li key={v.value}>
              <TagChip onRemove={() => remove(v)}>{v.label}</TagChip>
            </li>
          ))}
        </ul>
      )}

      <div className={value.length ? "relative" : "relative mt-1.5"}>
        <SearchIcon
          size={14}
          className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-[var(--muted-2)]"
        />
        <input
          id={id}
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          disabled={isLoading}
          placeholder={
            isLoading
              ? "Loading industries…"
              : "Search NACE 2.0 industries (e.g. wholesale, transport)"
          }
          aria-invalid={error ? true : undefined}
          aria-describedby={error ? `${id}-error` : undefined}
          className="w-full rounded-lg border border-[var(--line)] bg-white py-[11px] pl-10 pr-3.5 text-[14px] text-[var(--ink)] font-sans transition-[border-color,box-shadow] duration-150 placeholder:text-[var(--muted-2)] focus:border-[var(--orange)] focus:shadow-[0_0_0_4px_var(--focus-ring)] focus:outline-none disabled:bg-[var(--slate-2)]"
        />
      </div>

      {/* Suggestions. A list of buttons rather than a combobox: the chips above
          already report what is selected, and a full ARIA combobox would need
          managed focus for no gain over a plainly labelled list. */}
      {query && matches.length > 0 && (
        <ul className="mt-1.5 list-none overflow-hidden rounded-[10px] border border-[var(--line)] bg-white p-0 shadow-[var(--shadow-lg)]">
          {matches.map((m) => (
            <li key={m.value}>
              <button
                type="button"
                onClick={() => add(m)}
                className="flex w-full items-center gap-2 border-b border-[var(--line)] px-3.5 py-2.5 text-left text-[13.5px] text-[var(--ink)] font-sans last:border-b-0 hover:bg-[var(--slate-2)] focus-visible:bg-[var(--slate-2)] focus-visible:outline-none"
              >
                <PlusIcon className="shrink-0 text-[var(--orange)]" />
                <span className="min-w-0 flex-1">{m.label}</span>
              </button>
            </li>
          ))}
        </ul>
      )}

      {query && !isLoading && matches.length === 0 && (
        <p className="mt-2 rounded-lg bg-[var(--slate-2)] px-3.5 py-2.5 text-[12.5px] text-[var(--muted)] font-sans">
          {`No NACE 2.0 matches for "${query}".`}
        </p>
      )}

      {error && (
        <p id={`${id}-error`} role="alert" className="mt-1.5 text-[12px] text-[var(--coral-deep)] font-sans">
          {error}
        </p>
      )}
    </div>
  );
};

export default IndustryMultiSelect;
